import axios from 'axios';
import { jsPDF } from "jspdf";
import "jspdf-autotable";

/**
 * Serviço para geração de relatórios PDF para o dashboard de Mapeamentos LGPD
 * Simplificado e otimizado para funcionar com filtragem por cliente
 */
class MapeamentosPdfGenerator {
  constructor() {
    this.API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
    this.MAPEAMENTOS_API_PREFIX = '/api/dashboard/mapeamentos';
  }

  /**
   * Obtém headers de autenticação
   */
  getAuthHeaders() {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Valida e normaliza dados do relatório
   */
  validarDadosRelatorio(dados) {
    const dadosValidados = {
      estatisticas: {},
      questionarios: [],
      titulares: [],
      capitulos: [],
      versoes: []
    };

    const statsRaw = dados.estatisticas || dados.estatisticas_gerais || dados.data?.estatisticas || {};
    dadosValidados.estatisticas = {
      total_questionarios: parseInt(statsRaw.total_questionarios) || 0,
      questionarios_concluidos: parseInt(statsRaw.questionarios_concluidos) || 0,
      total_titulares: parseInt(statsRaw.total_titulares) || 0,
      dados_sensiveis: parseInt(statsRaw.dados_sensiveis) || 0,
      dados_comuns: parseInt(statsRaw.dados_comuns) || 0,
      areas_mapeadas: parseInt(statsRaw.areas_mapeadas) || 0,
      taxa_conclusao: parseFloat(statsRaw.taxa_conclusao) || 0
    };

    const questionariosRaw = dados.questionarios || dados.data?.questionarios || [];
    if (Array.isArray(questionariosRaw)) {
      dadosValidados.questionarios = questionariosRaw.filter(q => q && (q.nome || q._id));
    }

    const titularesRaw = dados.titulares || dados.data?.titulares || [];
    if (Array.isArray(titularesRaw)) {
      dadosValidados.titulares = titularesRaw.filter(t => t && (t.categoria || t._id));
    }

    const capitulosRaw = dados.capitulos || dados.data?.capitulos || [];
    if (Array.isArray(capitulosRaw)) {
      dadosValidados.capitulos = capitulosRaw.filter(c => c && (c.capitulo_id || c._id));
    }

    const versoesRaw = dados.versoes || dados.data?.versoes || [];
    if (Array.isArray(versoesRaw)) {
      dadosValidados.versoes = versoesRaw.filter(v => v && v._id);
    }

    if (dados.resumo) {
      dadosValidados.resumo = dados.resumo;
    }

    if (dados.recomendacoes && Array.isArray(dados.recomendacoes)) {
      dadosValidados.recomendacoes = dados.recomendacoes;
    }

    return dadosValidados;
  }

  /**
   * Recupera informações do cliente
   */
  async recuperarInfoCliente(clienteId) {
    if (!clienteId || clienteId.trim() === '') return null;
    
    try {
      const response = await axios.get(`${this.API_BASE_URL}/api/clientes/${clienteId}`, {
        headers: this.getAuthHeaders(),
        timeout: 5000
      });
      
      const cliente = response.data?.data || response.data;
      return cliente?.nome || cliente?.razao_social || `Cliente ${clienteId.substring(0, 8)}`;
    } catch (error) {
      return `Cliente ${clienteId.substring(0, 8)}`;
    }
  }

  /**
   * Gera um relatório PDF do dashboard de Mapeamentos com os dados do backend
   * @param {string} clienteId - ID do cliente para filtragem
   * @param {Object} filtros - Filtros para os dados
   * @returns {Promise<Blob>} - Blob do PDF gerado
   */
  async gerarRelatorioDashboard(clienteId, filtros = {}) {
    try {
      const pdfUrl = `${this.API_BASE_URL}${this.MAPEAMENTOS_API_PREFIX}/relatorios/gerar`;
      
      const payload = {
        tipo: filtros.tipo || 'geral',
        questionario_id: filtros.questionario_id,
        incluir_dados_sensiveis: filtros.incluir_dados_sensiveis ?? true
      };

      const params = {};
      if (clienteId && clienteId.trim() !== '') {
        params.cliente_id = clienteId.trim();
      }
      
      const response = await axios.post(pdfUrl, payload, {
        headers: {
          ...this.getAuthHeaders(),
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        params,
        timeout: 30000
      });
      
      if (!response.data) {
        throw new Error('Resposta vazia do servidor');
      }
      
      if (response.data.success === false) {
        throw new Error(response.data.message || response.data.error || 'Erro no processamento dos dados');
      }
      
      if (clienteId && !filtros.nomeCliente) {
        filtros.nomeCliente = await this.recuperarInfoCliente(clienteId);
      }
      
      const pdfBlob = await this.gerarPDF(response.data, filtros, clienteId);
      
      return pdfBlob;
      
    } catch (error) {
      let errorMessage = 'Falha ao gerar relatório PDF.';
      if (error.response?.data?.message) {
        errorMessage += ' ' + error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage += ' ' + error.response.data.error;
      } else if (error.message) {
        errorMessage += ' ' + error.message;
      }
      
      throw new Error(errorMessage);
    }
  }
  
  /**
   * Gera um PDF com os dados recebidos do backend
   * @param {Object} dadosResponse - Resposta completa do backend
   * @param {Object} filtros - Filtros aplicados
   * @param {string} clienteId - ID do cliente para contexto
   * @returns {Promise<Blob>} - Blob do PDF gerado
   */
  async gerarPDF(dadosResponse, filtros = {}, clienteId = null) {
    try {
      if (!dadosResponse || typeof dadosResponse !== 'object') {
        throw new Error('Dados de resposta inválidos ou ausentes');
      }
      
      const doc = new jsPDF();
      
      const margin = 15;
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const availableWidth = pageWidth - (margin * 2);
      
      let dadosRelatorio = {};
      if (dadosResponse.data) {
        dadosRelatorio = dadosResponse.data;
      } else if (dadosResponse.dados) {
        dadosRelatorio = dadosResponse.dados;
      } else {
        dadosRelatorio = dadosResponse;
      }
      
      const metadata = dadosResponse.metadata || {};
      const tipo = filtros.tipo || metadata.tipo || 'geral';
      
      const dadosValidados = this.validarDadosRelatorio(dadosRelatorio);
      
      let currentY = await this.adicionarCabecalho(doc, tipo, filtros, metadata, margin, pageWidth, clienteId);
      
      try {
        switch (tipo) {
          case 'geral':
            currentY = await this.gerarRelatorioGeral(doc, dadosValidados, currentY, margin, pageWidth, pageHeight, availableWidth);
            break;
          case 'questionario_detalhado':
            currentY = await this.gerarRelatorioQuestionario(doc, dadosValidados, currentY, margin, pageWidth, pageHeight, availableWidth);
            break;
          case 'titulares':
            currentY = await this.gerarRelatorioTitulares(doc, dadosValidados, currentY, margin, pageWidth, pageHeight, availableWidth);
            break;
          case 'estatisticas':
            currentY = await this.gerarRelatorioEstatisticas(doc, dadosValidados, currentY, margin, pageWidth, pageHeight, availableWidth);
            break;
          default:
            currentY = await this.gerarRelatorioGeral(doc, dadosValidados, currentY, margin, pageWidth, pageHeight, availableWidth);
        }
      } catch (contentError) {
        doc.setFontSize(12);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(255, 0, 0);
        doc.text('Erro ao gerar conteúdo do relatório:', margin, currentY);
        currentY += 10;
        doc.text(contentError.message || 'Erro desconhecido', margin, currentY);
        currentY += 20;
        
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text('Dados disponíveis:', margin, currentY);
        currentY += 8;
        doc.text(`Tipo: ${tipo}`, margin + 10, currentY);
        currentY += 6;
        doc.text(`Estrutura: ${Object.keys(dadosRelatorio).join(', ')}`, margin + 10, currentY);
        currentY += 20;
      }
      
      this.adicionarRodape(doc, pageWidth, pageHeight, margin, clienteId);
      
      return new Blob([doc.output('arraybuffer')], { type: 'application/pdf' });
    } catch (error) {
      throw error;
    }
  }

 /**
   * Adiciona cabeçalho ao PDF com informações do cliente
   */
  async adicionarCabecalho(doc, tipo, filtros, metadata, margin, pageWidth, clienteId, clienteNome = null) {
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    
    const titulos = {
      'geral': 'Relatório Geral - Mapeamento LGPD',
      'questionario_detalhado': 'Relatório Detalhado - Questionário LGPD',
      'titulares': 'Relatório de Titulares - LGPD',
      'estatisticas': 'Relatório de Estatísticas - LGPD'
    };
    
    const titulo = filtros.titulo_personalizado ||
                  metadata.titulo_personalizado ||
                  titulos[tipo] ||
                  'Relatório de Mapeamento LGPD';
                  
    doc.text(titulo, pageWidth / 2, margin + 10, { align: "center" });
    
    const isNomeValido = (nome) => {
      if (!nome || typeof nome !== 'string') return false;
      const nomeClean = nome.trim();
      
      if (nomeClean === '' || 
          nomeClean === 'Cliente' || 
          nomeClean === 'Cliente selecionado' ||
          nomeClean.startsWith('Cliente ') ||
          /^Cliente\s+[a-f0-9]{8,}$/i.test(nomeClean)) {
        return false;
      }
      
      return true;
    };
    
    let nomeExibicao = null;
    
    const candidatos = [
      clienteNome,
      filtros.nomeCliente,
      filtros.clienteNome,
      filtros.razao_social,
      filtros.nome_empresa
    ];
    
    for (const candidato of candidatos) {
      if (isNomeValido(candidato)) {
        nomeExibicao = candidato.trim();
        break;
      }
    }
    
    if (nomeExibicao) {
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 102, 204);
      doc.text(`Cliente: ${nomeExibicao}`, pageWidth / 2, margin + 25, { align: "center" });
    } else {
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(102, 102, 102);
      doc.text(`Relatório de Mapeamento LGPD`, pageWidth / 2, margin + 25, { align: "center" });
    }
    
    const dataAtual = new Date().toLocaleDateString("pt-BR");
    const horaAtual = new Date().toLocaleTimeString("pt-BR");
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(102, 102, 102);
    doc.text(`Gerado em: ${dataAtual} às ${horaAtual}`, pageWidth - margin, margin + 35, { align: "right" });
    
    if (clienteId && nomeExibicao) {
      doc.setFontSize(9);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(0, 102, 204);
      doc.text(`* Relatório específico do cliente`, pageWidth / 2, margin + 45, { align: "center" });
    } else if (clienteId) {
      doc.setFontSize(9);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(0, 102, 204);
      doc.text(`* Dados filtrados por cliente`, pageWidth / 2, margin + 45, { align: "center" });
    }
    
    doc.setDrawColor(0, 102, 204);
    doc.setLineWidth(0.5);
    doc.line(margin, margin + 50, pageWidth - margin, margin + 50);
    
    return margin + 60;
  }

  /**
   * Adiciona rodapé ao PDF
   */
  adicionarRodape(doc, pageWidth, pageHeight, margin, clienteId) {
    const totalPages = doc.internal.getNumberOfPages();
    const dataAtual = new Date().toLocaleDateString("pt-BR");
    
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.line(margin, pageHeight - 20, pageWidth - margin, pageHeight - 20);
      
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(
        `Página ${i} de ${totalPages}`, 
        pageWidth / 2, 
        pageHeight - 10, 
        { align: "center" }
      );
      
      doc.text(
        "Sistema de Mapeamento LGPD", 
        margin, 
        pageHeight - 10
      );
      
      const textoRodape = clienteId 
        ? `Relatório por cliente - ${dataAtual}` 
        : `Relatório geral - ${dataAtual}`;
      
      doc.text(
        textoRodape, 
        pageWidth - margin, 
        pageHeight - 10, 
        { align: "right" }
      );
    }
  }
  
  /**
   * Gera relatório geral
   */
  async gerarRelatorioGeral(doc, dados, currentY, margin, pageWidth, pageHeight, availableWidth) {
    const stats = dados.estatisticas || {};
    
    currentY = this.adicionarSecao(doc, "1. SUMÁRIO EXECUTIVO", currentY, margin, pageHeight);
    
    const totalQuestionarios = stats.total_questionarios || 0;
    const questionariosConcluidos = stats.questionarios_concluidos || 0;
    const totalTitulares = stats.total_titulares || 0;
    const dadosSensiveis = stats.dados_sensiveis || 0;
    const dadosComuns = stats.dados_comuns || 0;
    const percentualConclusao = totalQuestionarios > 0 ? Math.round((questionariosConcluidos / totalQuestionarios) * 100) : 0;
    
    const textoSumario = 
      `Este relatório apresenta o status atual do mapeamento de dados pessoais conforme a Lei Geral de Proteção de Dados (LGPD). ` +
      `Os dados apresentados foram filtrados conforme o cliente selecionado.\n\n` +
      `Situação atual: ${totalQuestionarios} questionários de mapeamento criados, dos quais ${questionariosConcluidos} estão concluídos ` +
      `(${percentualConclusao}% de conclusão).\n\n` +
      `O sistema possui ${totalTitulares} titulares de dados cadastrados, com ${dadosSensiveis} tipos de dados sensíveis ` +
      `e ${dadosComuns} tipos de dados comuns mapeados. Este mapeamento é fundamental para garantir a conformidade ` +
      `com a LGPD e a proteção adequada dos dados pessoais tratados pela organização.`;
    
    currentY = this.adicionarTexto(doc, textoSumario, currentY, margin, availableWidth);
    
    currentY = this.verificarNovaPagina(doc, currentY, pageHeight, margin);
    currentY = this.adicionarSecao(doc, "2. ESTATÍSTICAS GERAIS", currentY, margin, pageHeight);
    
    const tabelaEstatisticas = [
      ["Indicador", "Quantidade", "Status"],
      ["Total de Questionários", totalQuestionarios.toString(), totalQuestionarios > 0 ? "✓" : "⚠"],
      ["Questionários Concluídos", questionariosConcluidos.toString(), `${percentualConclusao}%`],
      ["Total de Titulares", totalTitulares.toString(), totalTitulares > 0 ? "✓" : "⚠"],
      ["Dados Sensíveis Mapeados", dadosSensiveis.toString(), dadosSensiveis > 0 ? "Atenção" : "OK"],
      ["Dados Comuns Mapeados", dadosComuns.toString(), dadosComuns > 0 ? "✓" : "⚠"],
      ["Áreas Mapeadas", (stats.areas_mapeadas || 0).toString(), stats.areas_mapeadas > 0 ? "✓" : "⚠"],
      ["Taxa de Conclusão", `${percentualConclusao}%`, percentualConclusao >= 80 ? "Bom" : percentualConclusao >= 50 ? "Regular" : "Baixo"]
    ];
    
    currentY = this.adicionarTabela(doc, tabelaEstatisticas, currentY, margin, availableWidth, [66, 139, 202]);
    
    if (dados.questionarios && dados.questionarios.length > 0) {
      currentY = this.verificarNovaPagina(doc, currentY, pageHeight, margin);
      currentY = this.adicionarSecao(doc, `3. QUESTIONÁRIOS DE MAPEAMENTO (${dados.questionarios.length})`, currentY, margin, pageHeight);
      
      const questionariosParaTabela = dados.questionarios.slice(0, 15);
      const tabelaQuestionarios = [
        ["Nome", "Abordagem", "Data de Criação", "Status"],
        ...questionariosParaTabela.map(q => [
          (q.nome || 'Sem nome').substring(0, 40),
          q.abordagem || 'N/A',
          q.created_at ? new Date(q.created_at).toLocaleDateString('pt-BR') : 'N/A',
          q.inativo ? 'Inativo' : 'Ativo'
        ])
      ];
      
      currentY = this.adicionarTabela(doc, tabelaQuestionarios, currentY, margin, availableWidth, [66, 139, 202]);
      
      if (dados.questionarios.length > 15) {
        currentY = this.adicionarTextoItalico(doc, `... e mais ${dados.questionarios.length - 15} questionários não exibidos.`, currentY, margin);
      }
    } else {
      currentY = this.verificarNovaPagina(doc, currentY, pageHeight, margin);
      currentY = this.adicionarSecao(doc, "3. QUESTIONÁRIOS DE MAPEAMENTO", currentY, margin, pageHeight);
      currentY = this.adicionarTexto(doc, "Nenhum questionário encontrado para este cliente.", currentY, margin, availableWidth, 12);
    }
    
    if (dados.titulares && dados.titulares.length > 0) {
      currentY = this.verificarNovaPagina(doc, currentY, pageHeight, margin);
      currentY = this.adicionarSecao(doc, `4. TITULARES DE DADOS (${dados.titulares.length})`, currentY, margin, pageHeight);
      
      const titularesParaTabela = dados.titulares.slice(0, 15);
      const tabelaTitulares = [
        ["Categoria", "Quantidade", "Faixa Etária", "Tipos de Dados"],
        ...titularesParaTabela.map(t => [
          (t.categoria || 'Sem categoria').substring(0, 30),
          t.quantidade || 'N/A',
          t.faixa_etaria ? (Array.isArray(t.faixa_etaria) ? t.faixa_etaria.join(', ') : t.faixa_etaria).substring(0, 25) : 'N/A',
          t.dados ? (Array.isArray(t.dados) ? t.dados.length.toString() : '0') : '0'
        ])
      ];
      
      currentY = this.adicionarTabela(doc, tabelaTitulares, currentY, margin, availableWidth, [76, 175, 80]);
      
      if (dados.titulares.length > 15) {
        currentY = this.adicionarTextoItalico(doc, `... e mais ${dados.titulares.length - 15} titulares não exibidos.`, currentY, margin);
      }
    } else {
      currentY = this.verificarNovaPagina(doc, currentY, pageHeight, margin);
      currentY = this.adicionarSecao(doc, "4. TITULARES DE DADOS", currentY, margin, pageHeight);
      currentY = this.adicionarTexto(doc, "Nenhum titular de dados encontrado para este cliente.", currentY, margin, availableWidth, 12);
    }
    
    return currentY;
  }
  
  /**
   * Gera relatório de questionário detalhado
   */
  async gerarRelatorioQuestionario(doc, dados, currentY, margin, pageWidth, pageHeight, availableWidth) {
    currentY = this.adicionarSecao(doc, "1. INFORMAÇÕES DO QUESTIONÁRIO", currentY, margin, pageHeight);
    
    if (dados.questionario || (dados.questionarios && dados.questionarios.length > 0)) {
      const questionario = dados.questionario || dados.questionarios[0];
      
      const tabelaInfo = [
        ["Campo", "Valor"],
        ["Nome", questionario.nome || 'N/A'],
        ["Abordagem", questionario.abordagem || 'N/A'],
        ["Data de Criação", questionario.created_at ? new Date(questionario.created_at).toLocaleDateString('pt-BR') : 'N/A'],
        ["Última Atualização", questionario.updated_at ? new Date(questionario.updated_at).toLocaleDateString('pt-BR') : 'N/A'],
        ["Status", questionario.inativo ? 'Inativo' : 'Ativo']
      ];
      
      if (questionario.cliente_id) {
        const clienteNome = typeof questionario.cliente_id === 'object' && questionario.cliente_id.nome 
          ? questionario.cliente_id.nome 
          : questionario.cliente_id;
        tabelaInfo.push(["Cliente", clienteNome]);
      }
      
      if (questionario.areas && Array.isArray(questionario.areas)) {
        tabelaInfo.push(["Áreas Vinculadas", questionario.areas.length.toString()]);
      }
      
      currentY = this.adicionarTabela(doc, tabelaInfo, currentY, margin, availableWidth, [66, 139, 202]);
    } else {
      currentY = this.adicionarTexto(doc, "Informações do questionário não disponíveis.", currentY, margin, availableWidth, 12);
    }
    
    if (dados.capitulos && dados.capitulos.length > 0) {
      currentY = this.verificarNovaPagina(doc, currentY, pageHeight, margin);
      currentY = this.adicionarSecao(doc, `2. CAPÍTULOS/PROCESSOS (${dados.capitulos.length})`, currentY, margin, pageHeight);
      
      dados.capitulos.forEach((capitulo, index) => {
        currentY = this.verificarNovaPagina(doc, currentY, pageHeight, margin, 80);
        
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        const nomeCapitulo = capitulo.capitulo_id?.description || 
                            capitulo.capitulo?.description || 
                            capitulo.nome ||
                            `Capítulo ${index + 1}`;
        doc.text(`${index + 1}. ${nomeCapitulo}`, margin, currentY);
        currentY += 12;
        
        if (capitulo.descricao_personalizada) {
          currentY = this.adicionarTexto(doc, `Descrição: ${capitulo.descricao_personalizada}`, currentY, margin + 5, availableWidth - 10, 10);
        }
        
        if (capitulo.areas_id && Array.isArray(capitulo.areas_id) && capitulo.areas_id.length > 0) {
          const areas = capitulo.areas_id.map(a => a.description || a.nome || 'Área').join(', ');
          currentY = this.adicionarTextoItalico(doc, `Áreas: ${areas}`, currentY, margin + 5);
        }
        
        currentY += 8;
      });
    } else {
      currentY = this.verificarNovaPagina(doc, currentY, pageHeight, margin);
      currentY = this.adicionarSecao(doc, "2. CAPÍTULOS/PROCESSOS", currentY, margin, pageHeight);
      currentY = this.adicionarTexto(doc, "Nenhum capítulo encontrado para este questionário.", currentY, margin, availableWidth, 12);
    }
    
    if (dados.titulares && dados.titulares.length > 0) {
      currentY = this.verificarNovaPagina(doc, currentY, pageHeight, margin);
      currentY = this.adicionarSecao(doc, `3. TITULARES DE DADOS (${dados.titulares.length})`, currentY, margin, pageHeight);
      
      const tabelaTitulares = [
        ["Categoria", "Quantidade", "Faixa Etária", "Dados"],
        ...dados.titulares.slice(0, 10).map(t => [
          (t.categoria || 'N/A').substring(0, 25),
          t.quantidade || 'N/A',
          t.faixa_etaria ? (Array.isArray(t.faixa_etaria) ? t.faixa_etaria.join(', ') : t.faixa_etaria).substring(0, 20) : 'N/A',
         t.dados ? (Array.isArray(t.dados) ? t.dados.length.toString() : '0') : '0'
       ])
     ];
     
     currentY = this.adicionarTabela(doc, tabelaTitulares, currentY, margin, availableWidth, [76, 175, 80]);
     
     if (dados.titulares.length > 10) {
       currentY = this.adicionarTextoItalico(doc, `... e mais ${dados.titulares.length - 10} titulares não exibidos.`, currentY, margin);
     }
   } else {
     currentY = this.verificarNovaPagina(doc, currentY, pageHeight, margin);
     currentY = this.adicionarSecao(doc, "3. TITULARES DE DADOS", currentY, margin, pageHeight);
     currentY = this.adicionarTexto(doc, "Nenhum titular de dados encontrado para este questionário.", currentY, margin, availableWidth, 12);
   }
   
   if (dados.resumo || dados.estatisticas) {
     currentY = this.verificarNovaPagina(doc, currentY, pageHeight, margin);
     currentY = this.adicionarSecao(doc, "4. RESUMO EXECUTIVO", currentY, margin, pageHeight);
     
     const resumo = dados.resumo || {};
     const stats = dados.estatisticas || {};
     
     const tabelaResumo = [
       ["Métrica", "Valor", "Observação"],
       ["Total de Capítulos", (resumo.totalCapitulos || dados.capitulos?.length || 0).toString(), "Processos mapeados"],
       ["Total de Titulares", (resumo.totalTitulares || dados.titulares?.length || 0).toString(), "Indivíduos identificados"],
       ["Total de Versões", (resumo.totalVersoes || dados.versoes?.length || 0).toString(), "Versões do questionário"]
     ];
     
     if (resumo.percentual_completo !== undefined) {
       tabelaResumo.push(["Percentual Completo", `${resumo.percentual_completo}%`, resumo.percentual_completo >= 80 ? "Bom" : "Regular"]);
     }
     
     if (stats.taxa_conclusao !== undefined) {
       tabelaResumo.push(["Taxa de Conclusão", `${stats.taxa_conclusao}%`, stats.taxa_conclusao >= 80 ? "Bom" : "Regular"]);
     }
     
     currentY = this.adicionarTabela(doc, tabelaResumo, currentY, margin, availableWidth, [255, 193, 7], 0);
   }
   
   return currentY;
 }
 
 /**
 * Gera relatório de titulares
 */
async gerarRelatorioTitulares(doc, dados, currentY, margin, pageWidth, pageHeight, availableWidth) {
  if (!dados.titulares || dados.titulares.length === 0) {
    currentY = this.adicionarTexto(doc, 'Nenhum titular de dados encontrado para este cliente.', currentY, margin, availableWidth, 12);
    return currentY + 20;
  }
  
  currentY = this.adicionarSecao(doc, `1. TITULARES DE DADOS (${dados.titulares.length})`, currentY, margin, pageHeight);
  
  if (dados.estatisticas && typeof dados.estatisticas === 'object') {
    const stats = dados.estatisticas;
    const tabelaEstatisticas = [
      ["Estatística", "Valor", "Observação"],
      ["Total de Titulares", (stats.total || dados.titulares.length).toString(), "Indivíduos cadastrados"],
      ["Categorias Únicas", Object.keys(stats.por_categoria || {}).length.toString(), "Tipos diferentes"],
      ["Total de Dados Tratados", (stats.total_dados_tratados || 'N/A').toString(), "Dados pessoais mapeados"]
    ];
    
    if (stats.por_quantidade && Object.keys(stats.por_quantidade).length > 0) {
      const quantidadeMaisComum = Object.entries(stats.por_quantidade)
        .sort(([,a], [,b]) => b - a)[0];
      tabelaEstatisticas.push(["Faixa Mais Comum", quantidadeMaisComum[0], `${quantidadeMaisComum[1]} ocorrências`]);
    }
    
    currentY = this.adicionarTabela(doc, tabelaEstatisticas, currentY, margin, availableWidth, [52, 168, 83]);
  }
  
  currentY = this.adicionarSecao(doc, "2. DETALHAMENTO POR TITULAR", currentY, margin, pageHeight);
  
  const titularesParaExibir = dados.titulares.slice(0, 20);
  
  titularesParaExibir.forEach((titular, index) => {
    currentY = this.verificarNovaPagina(doc, currentY, pageHeight, margin, 140);
    
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(52, 168, 83);
    doc.text(`${index + 1}. ${titular.categoria || 'Categoria não informada'}`, margin, currentY);
    currentY += 12;
    
    doc.setTextColor(0, 0, 0);
    const infoBasica = [
      ["Campo", "Valor"],
      ["Quantidade", titular.quantidade || 'N/A'],
      ["Faixa Etária", titular.faixa_etaria ? (Array.isArray(titular.faixa_etaria) ? titular.faixa_etaria.join(', ') : titular.faixa_etaria) : 'N/A'],
      ["Data de Cadastro", titular.created_at ? new Date(titular.created_at).toLocaleDateString('pt-BR') : 'N/A'],
      ["Status", titular.inativo ? 'Inativo' : 'Ativo']
    ];
    
    doc.autoTable({
      startY: currentY,
      head: [infoBasica[0]],
      body: infoBasica.slice(1),
      theme: 'grid',
      headStyles: {
        fillColor: [52, 168, 83],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9
      },
      styles: {
        cellPadding: 3,
        fontSize: 9,
        textColor: [0, 0, 0]
      },
      columnStyles: {
        0: { cellWidth: availableWidth * 0.30, fontStyle: 'bold' },
        1: { cellWidth: availableWidth * 0.70 }
      },
      margin: { left: margin, right: margin }
    });
    
    currentY = doc.lastAutoTable.finalY + 8;
    
    if (titular.dados && Array.isArray(titular.dados) && titular.dados.length > 0) {
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(76, 175, 80);
      doc.text('Dados Pessoais Tratados:', margin, currentY);
      currentY += 8;
      
      const dadosParaExibir = titular.dados.slice(0, 5);
      const dadosData = [
        ["Tipo", "Origem", "Consentimento", "Frequência"],
        ...dadosParaExibir.map(dado => [
          (dado.dado_id?.nome || dado.tipo || 'N/A').substring(0, 15),
          (dado.origem || 'N/A').substring(0, 12),
          (dado.consentimento_titular || 'N/A').substring(0, 12),
          (dado.frequencia || 'N/A').substring(0, 12)
        ])
      ];
      
      doc.autoTable({
        startY: currentY,
        head: [dadosData[0]],
        body: dadosData.slice(1),
        theme: 'grid',
        headStyles: {
          fillColor: [76, 175, 80],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 8
        },
        styles: {
          cellPadding: 2,
          fontSize: 8,
          textColor: [0, 0, 0]
        },
        margin: { left: margin + 10, right: margin }
      });
      
      currentY = doc.lastAutoTable.finalY + 5;
      
      if (titular.dados.length > 5) {
        currentY = this.adicionarTextoItalico(doc, `... e mais ${titular.dados.length - 5} tipos de dados.`, currentY, margin + 10, 8);
      }
    }
    
    const finalidades = [];
    if (titular.finalidade_coleta) finalidades.push(`Coleta: ${titular.finalidade_coleta.substring(0, 100)}`);
    if (titular.finalidade_processamento) finalidades.push(`Processamento: ${titular.finalidade_processamento.substring(0, 100)}`);
    
    if (finalidades.length > 0) {
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(100, 100, 100);
      doc.text('Finalidades:', margin, currentY);
      currentY += 6;
      
      finalidades.forEach(finalidade => {
        currentY = this.adicionarTextoItalico(doc, `• ${finalidade}`, currentY, margin + 5, 8);
      });
    }
    
    currentY += 15;
  });
  
  if (dados.titulares.length > 20) {
    currentY = this.adicionarTextoItalico(doc, `... e mais ${dados.titulares.length - 20} titulares não exibidos por limitação de espaço.`, currentY, margin);
  }
  
  return currentY;
}

/**
 * Gera relatório de estatísticas
 */
async gerarRelatorioEstatisticas(doc, dados, currentY, margin, pageWidth, pageHeight, availableWidth) {
  const stats = dados.estatisticas || {};
  
  if (Object.keys(stats).length === 0) {
    currentY = this.adicionarTexto(doc, 'Estatísticas não disponíveis para este cliente.', currentY, margin, availableWidth, 12);
    return currentY + 20;
  }
  
  currentY = this.adicionarSecao(doc, "1. INDICADORES DE MAPEAMENTO LGPD", currentY, margin, pageHeight);
  
  const estatisticasPrincipais = [
    ["Métrica", "Quantidade", "Percentual", "Status"],
    ["Questionários Totais", (stats.total_questionarios || 0).toString(), "100%", stats.total_questionarios > 0 ? "✓" : "⚠"],
    ["Questionários Concluídos", (stats.questionarios_concluidos || 0).toString(), 
     stats.total_questionarios > 0 ? `${Math.round((stats.questionarios_concluidos / stats.total_questionarios) * 100)}%` : "0%",
     stats.questionarios_concluidos > 0 ? "✓" : "⚠"],
    ["Titulares Cadastrados", (stats.total_titulares || 0).toString(), "-", stats.total_titulares > 0 ? "✓" : "⚠"],
    ["Dados Sensíveis", (stats.dados_sensiveis || 0).toString(), "-", stats.dados_sensiveis > 0 ? "Atenção" : "OK"],
    ["Dados Comuns", (stats.dados_comuns || 0).toString(), "-", stats.dados_comuns > 0 ? "✓" : "⚠"],
    ["Áreas Mapeadas", (stats.areas_mapeadas || 0).toString(), "-", stats.areas_mapeadas > 2 ? "✓" : "⚠"]
  ];
  
  currentY = this.adicionarTabela(doc, estatisticasPrincipais, currentY, margin, availableWidth, [66, 139, 202]);
  
  currentY = this.verificarNovaPagina(doc, currentY, pageHeight, margin);
  currentY = this.adicionarSecao(doc, "2. ANÁLISE DE CONFORMIDADE LGPD", currentY, margin, pageHeight);
  
  const taxaConclusao = stats.total_questionarios > 0 ?
    (stats.questionarios_concluidos / stats.total_questionarios) * 100 : 0;
 
  let nivelConformidade = 'Baixo';
  let corStatus = '⚠';
  
  if (taxaConclusao >= 90) {
    nivelConformidade = 'Excelente';
    corStatus = '✓';
  } else if (taxaConclusao >= 70) {
    nivelConformidade = 'Bom';
    corStatus = '✓';
  } else if (taxaConclusao >= 50) {
    nivelConformidade = 'Regular';
    corStatus = '◐';
  }
  
  const conformidadeData = [
    ["Aspecto", "Status", "Nível", "Observação"],
    ["Taxa de Conclusão", `${taxaConclusao.toFixed(1)}%`, nivelConformidade, corStatus],
    ["Cobertura de Dados", 
     (stats.dados_sensiveis + stats.dados_comuns) > 0 ? 'Dados mapeados' : 'Sem dados mapeados',
     stats.dados_sensiveis > 0 ? 'Completo' : 'Parcial',
     stats.dados_sensiveis > 0 ? 'Inclui dados sensíveis' : 'Apenas dados comuns'],
    ["Mapeamento de Áreas", 
     stats.areas_mapeadas > 0 ? 'Áreas mapeadas' : 'Sem áreas mapeadas',
     stats.areas_mapeadas > 2 ? 'Adequado' : 'Insuficiente',
     `${stats.areas_mapeadas || 0} área(s) identificada(s)`],
    ["Cadastro de Titulares",
     stats.total_titulares > 0 ? 'Titulares cadastrados' : 'Sem titulares',
     stats.total_titulares > 5 ? 'Adequado' : stats.total_titulares > 0 ? 'Inicial' : 'Ausente',
     `${stats.total_titulares || 0} titular(es) mapeado(s)`]
  ];
  
  currentY = this.adicionarTabela(doc, conformidadeData, currentY, margin, availableWidth, [76, 175, 80]);
  
  currentY = this.verificarNovaPagina(doc, currentY, pageHeight, margin, 120);
  currentY = this.adicionarSecao(doc, "3. RECOMENDAÇÕES PARA CONFORMIDADE", currentY, margin, pageHeight);
  
  let recomendacoes = [];
  
  if (dados.recomendacoes && Array.isArray(dados.recomendacoes) && dados.recomendacoes.length > 0) {
    recomendacoes = dados.recomendacoes.map(rec => 
      `• [${rec.prioridade || 'Média'}] ${rec.descricao} ${rec.categoria ? `(${rec.categoria})` : ''}`
    );
  } else {
    if (taxaConclusao < 100) {
      const faltam = stats.total_questionarios - stats.questionarios_concluidos;
      recomendacoes.push(`• [Alta] Finalizar ${faltam} questionário(s) pendente(s) para garantir mapeamento completo`);
    }
    
    if (stats.dados_sensiveis === 0) {
      recomendacoes.push('• [Média] Verificar se existem dados sensíveis não mapeados na organização (CPF, dados de saúde, etc.)');
    }
    
    if (stats.total_titulares === 0) {
      recomendacoes.push('• [Alta] Cadastrar titulares de dados para atender aos requisitos da LGPD');
    } else if (stats.total_titulares < 5) {
      recomendacoes.push('• [Média] Expandir o cadastro de titulares para cobrir todas as categorias de dados pessoais');
    }
    
    if (stats.areas_mapeadas < 3) {
      recomendacoes.push('• [Média] Considerar expandir o mapeamento para mais áreas da organização (RH, Vendas, TI, etc.)');
    }
    
    if (stats.dados_sensiveis > 0 && stats.total_titulares === 0) {
      recomendacoes.push('• [Alta] Dados sensíveis identificados requerem mapeamento detalhado de titulares');
    }
    
    if (taxaConclusao >= 90 && stats.total_titulares > 0 && stats.dados_sensiveis >= 0) {
      recomendacoes.push('• [Baixa] Manter a conformidade atual e revisar periodicamente o mapeamento (trimestral)');
      recomendacoes.push('• [Baixa] Implementar processo de atualização contínua para novos tratamentos de dados');
    }
    
    if (recomendacoes.length === 0) {
      recomendacoes.push('• [Baixa] Sistema em conformidade básica - implementar melhorias incrementais');
    }
  }
  
  recomendacoes.forEach(rec => {
    currentY = this.adicionarTexto(doc, rec, currentY, margin, availableWidth, 11);
    currentY += 6;
  });
  
  currentY = this.verificarNovaPagina(doc, currentY, pageHeight, margin, 80);
  currentY = this.adicionarSecao(doc, "4. PRÓXIMOS PASSOS", currentY, margin, pageHeight);
  
  const proximosPassos = [
    '• Revisar e atualizar questionários pendentes',
    '• Validar informações de titulares cadastrados',
    '• Implementar controles de segurança adequados',
    '• Estabelecer processo de monitoramento contínuo',
    '• Treinar equipes sobre procedimentos LGPD'
  ];
  
  proximosPassos.forEach(passo => {
    currentY = this.adicionarTexto(doc, passo, currentY, margin, availableWidth, 11);
    currentY += 5;
  });
  
  return currentY;
}

// ============== MÉTODOS AUXILIARES ==============

/**
 * Adiciona uma seção com título
 */
adicionarSecao(doc, titulo, currentY, margin, pageHeight) {
  if (currentY > pageHeight - 100) {
    doc.addPage();
    currentY = margin;
  }
  
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(0, 102, 204);
  doc.text(titulo, margin, currentY);
  
  doc.setDrawColor(0, 102, 204);
  doc.setLineWidth(0.5);
  doc.line(margin, currentY + 3, margin + 120, currentY + 3);
  
  doc.setTextColor(0, 0, 0);
  return currentY + 18;
}

/**
 * Adiciona texto com quebra de linha automática
 */
adicionarTexto(doc, texto, currentY, margin, availableWidth, fontSize = 11) {
  doc.setFontSize(fontSize);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(0, 0, 0);
  const splitText = doc.splitTextToSize(texto, availableWidth);
  doc.text(splitText, margin, currentY);
  return currentY + (splitText.length * (fontSize * 0.6)) + 8;
}

/**
 * Adiciona texto em itálico
 */
adicionarTextoItalico(doc, texto, currentY, margin, fontSize = 10) {
  doc.setFontSize(fontSize);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(102, 102, 102);
  doc.text(texto, margin, currentY);
  doc.setTextColor(0, 0, 0);
  return currentY + fontSize + 2;
}

/**
 * Adiciona uma tabela
 */
adicionarTabela(doc, dados, currentY, margin, availableWidth, corCabecalho, corTexto = 255) {
  if (!dados || dados.length < 2) {
    return currentY + 10;
  }
  
  doc.autoTable({
    startY: currentY,
    head: [dados[0]],
    body: dados.slice(1),
    theme: 'grid',
    headStyles: {
      fillColor: corCabecalho,
      textColor: corTexto,
      fontStyle: 'bold',
      fontSize: 10,
      cellPadding: 4
    },
    styles: {
      cellPadding: 3,
      fontSize: 9,
      textColor: [0, 0, 0],
      lineColor: [200, 200, 200],
      lineWidth: 0.1
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245]
    },
    margin: { left: margin, right: margin },
    tableWidth: 'auto',
    columnStyles: {
      0: { fontStyle: 'bold' }
    }
  });
  
  return doc.lastAutoTable.finalY + 15;
}

/**
 * Verifica se precisa de nova página
 */
verificarNovaPagina(doc, currentY, pageHeight, margin, espacoNecessario = 100) {
  if (currentY > pageHeight - espacoNecessario) {
    doc.addPage();
    return margin;
  }
  return currentY;
}

/**
 * Gera um relatório e abre para visualização
 */
async gerarEVisualizar(clienteId, filtros = {}) {
  try {
    const pdfBlob = await this.gerarRelatorioDashboard(clienteId, filtros);
    const url = window.URL.createObjectURL(pdfBlob);
    
    const tipo = filtros.tipo || 'geral';
    const clienteTexto = clienteId ? `cliente-${clienteId.substring(0, 8)}` : 'geral';
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
    const filename = `relatorio-mapeamento-${tipo}-${clienteTexto}-${timestamp}.pdf`;
    
    const newWindow = window.open('', '_blank', 'width=1200,height=900,toolbar=yes,menubar=yes,resizable=yes,scrollbars=yes');
    
    if (newWindow) {
      newWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>${filename}</title>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            body { margin: 0; padding: 0; background-color: #f5f5f5; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
            .container { display: flex; flex-direction: column; height: 100vh; }
            .header { 
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              color: white;
              padding: 12px 20px; 
              box-shadow: 0 2px 10px rgba(0,0,0,0.1); 
              display: flex; 
              justify-content: space-between; 
              align-items: center; 
            }
            .header h1 { margin: 0; font-size: 16px; font-weight: 600; }
            .header .actions { display: flex; gap: 8px; }
            .header button { 
              padding: 8px 16px; 
              border: 1px solid rgba(255,255,255,0.3); 
              background: rgba(255,255,255,0.1); 
              color: white;
              border-radius: 6px; 
              cursor: pointer; 
              font-size: 12px; 
              transition: background 0.2s;
            }
            .header button:hover { background: rgba(255,255,255,0.2); }
            .header button.primary { 
              background: rgba(255,255,255,0.9); 
              color: #333;
            }
            .client-info { 
              background: #e6f7ff; 
              padding: 8px 20px; 
              border-bottom: 1px solid #91d5ff; 
              font-size: 12px; 
              color: #1890ff; 
              text-align: center;
            }
            iframe { flex: 1; width: 100%; border: none; background: white; }
            .loading { 
              display: flex; 
              justify-content: center; 
              align-items: center; 
              height: 200px; 
              color: #666; 
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>📊 ${filename}</h1>
              <div class="actions">
                <button onclick="window.print()" title="Imprimir relatório">🖨️ Imprimir</button>
                <button onclick="downloadPDF()" class="primary" title="Baixar arquivo PDF">💾 Baixar</button>
                <button onclick="window.close()" title="Fechar janela">✖️ Fechar</button>
              </div>
            </div>
            ${clienteId ? `<div class="client-info">📋 Relatório filtrado por cliente: ${clienteId}</div>` : ''}
            <div class="loading" id="loading">Carregando relatório...</div>
            <iframe src="${url}" style="display: none;" onload="showPDF()" onerror="showError()"></iframe>
          </div>
          
          <script>
            function downloadPDF() {
              const link = document.createElement('a');
              link.href = '${url}';
              link.download = '${filename}';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
            
            function showPDF() {
              document.getElementById('loading').style.display = 'none';
              document.querySelector('iframe').style.display = 'block';
            }
            
            function showError() {
              document.getElementById('loading').innerHTML = '❌ Erro ao carregar o relatório';
            }
            
            window.addEventListener('beforeunload', function() {
              URL.revokeObjectURL('${url}');
            });
            
            setTimeout(() => {
              URL.revokeObjectURL('${url}');
            }, 60000);
          </script>
        </body>
        </html>
      `);
      
      newWindow.document.close();
      newWindow.focus();
      
      setTimeout(() => URL.revokeObjectURL(url), 120000);
    } else {
      throw new Error('Popup bloqueado. Fazendo download direto.');
    }
    
  } catch (error) {
    await this.gerarEBaixar(clienteId, filtros);
  }
}

/**
 * Gera um relatório e salva como arquivo PDF
 */
async gerarEBaixar(clienteId, filtros = {}, filename = null) {
  try {
    if (!filename) {
      const tipo = filtros.tipo || 'geral';
      const clienteTexto = clienteId ? `cliente-${clienteId.substring(0, 8)}` : 'geral';
      const timestamp = new Date().toISOString().slice(0, 10);
      filename = `relatorio-mapeamento-${tipo}-${clienteTexto}-${timestamp}.pdf`;
    }
    
    const pdfBlob = await this.gerarRelatorioDashboard(clienteId, filtros);
    const url = window.URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
  } catch (error) {
    throw error;
  }
}

/**
 * Gera relatório com dados locais (fallback)
 */
async gerarRelatorioLocal(dados, filtros = {}, clienteId = null) {
  try {
    const dadosFormatados = {
      success: true,
      data: dados,
      metadata: {
        tipo: filtros.tipo || 'geral',
        gerado_em: new Date().toISOString(),
        inclui_dados_sensiveis: filtros.incluir_dados_sensiveis ?? true,
        cliente_id: clienteId,
        fonte: 'local'
      }
    };
    
    const pdfBlob = await this.gerarPDF(dadosFormatados, filtros, clienteId);
    return pdfBlob;
  } catch (error) {
    throw error;
  }
}

/**
 * Método utilitário para verificar se o serviço está disponível
 */
async verificarDisponibilidade() {
  try {
    const healthUrl = `${this.API_BASE_URL}${this.MAPEAMENTOS_API_PREFIX}/health`;
    
    const response = await axios.get(healthUrl, {
      timeout: 5000,
      headers: { 
        'Accept': 'application/json',
        ...this.getAuthHeaders()
      }
    });
    
    return response.status === 200 && (response.data.success || response.data.status === 'ok');
  } catch (error) {
    return false;
  }
}

/**
 * Método para testar geração de PDF com dados mockados
 */
async testarGeracao(clienteId = null) {
  const dadosMock = {
    estatisticas: {
      total_questionarios: 5,
      questionarios_concluidos: 3,
      total_titulares: 12,
      dados_sensiveis: 8,
      dados_comuns: 15,
      areas_mapeadas: 4,
      taxa_conclusao: 60
    },
    questionarios: [
      {
        _id: 'test1',
        nome: 'Questionário de Clientes - Teste',
        abordagem: 'detalhada',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        inativo: false,
        cliente_id: clienteId
      },
      {
        _id: 'test2',
        nome: 'Questionário de Funcionários - Teste',
        abordagem: 'simplificada',
        created_at: new Date(Date.now() - 86400000).toISOString(),
        inativo: false,
        cliente_id: clienteId
      }
    ],
    titulares: [
      {
        _id: 'titular1',
        categoria: 'Clientes',
        quantidade: '100 a 999 pessoas',
        faixa_etaria: ['Adultos (18 a 60 anos)', 'Idosos (60+ anos)'],
        created_at: new Date().toISOString(),
        inativo: false,
        dados: [
          { 
            dado_id: { nome: 'CPF', tipo: 'sensivel' },
            origem: 'Titular', 
            consentimento_titular: 'Sim', 
            frequencia: 'Uma vez' 
          },
          { 
            dado_id: { nome: 'Email', tipo: 'comum' },
            origem: 'Titular', 
            consentimento_titular: 'Sim', 
            frequencia: 'Mensal' 
          }
        ],
        finalidade_coleta: 'Prestação de serviços e comunicação comercial',
        finalidade_processamento: 'Análise de perfil e segmentação'
      },
      {
        _id: 'titular2',
        categoria: 'Funcionários',
        quantidade: '10 a 49 pessoas',
        faixa_etaria: ['Adultos (18 a 60 anos)'],
        created_at: new Date(Date.now() - 172800000).toISOString(),
        inativo: false,
        dados: [
          { 
            dado_id: { nome: 'RG', tipo: 'sensivel' },
            origem: 'Titular', 
            consentimento_titular: 'Não aplicável', 
            frequencia: 'Uma vez' 
          }
        ],
        finalidade_coleta: 'Cumprimento de obrigações trabalhistas'
      }
    ],
    capitulos: [
      {
        _id: 'cap1',
        capitulo_id: { description: 'Coleta de Dados Pessoais' },
        areas_id: [
          { description: 'Atendimento ao Cliente' },
          { description: 'Vendas' }
        ],
        descricao_personalizada: 'Processo de coleta de dados durante o atendimento'
      }
    ],
    versoes: [
      {
        _id: 'v1',
        questionario_id: 'test1',
        status: 'Concluído',
        created_at: new Date().toISOString(),
        respostas: 25
      }
    ],
    resumo: {
      totalCapitulos: 1,
      totalTitulares: 2,
      totalVersoes: 1,
      percentual_completo: 75
    }
  };

  try {
    const pdfBlob = await this.gerarRelatorioLocal(dadosMock, { tipo: 'geral' }, clienteId);
    return pdfBlob;
  } catch (error) {
    throw error;
  }
}

/**
 * Método para gerar relatório de múltiplos tipos
 */
async gerarRelatorioMultiplo(clienteId, tipos = ['geral'], filtros = {}) {
  try {
    if (!Array.isArray(tipos) || tipos.length === 0) {
      throw new Error('Tipos de relatório devem ser fornecidos em array');
    }

    const doc = new jsPDF();
    const margin = 15;
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const availableWidth = pageWidth - (margin * 2);
    
    const nomeCliente = await this.recuperarInfoCliente(clienteId);
    
    let currentY = await this.adicionarCabecalho(doc, 'multiplo', { 
      ...filtros, 
      nomeCliente,
      titulo_personalizado: 'Relatório Completo - Mapeamento LGPD'
    }, {}, margin, pageWidth, clienteId);
    
    currentY = this.adicionarSecao(doc, "ÍNDICE", currentY, margin, pageHeight);
    tipos.forEach((tipo, index) => {
      const tipoLabel = {
        'geral': 'Relatório Geral',
        'questionario_detalhado': 'Questionário Detalhado',
        'titulares': 'Relatório de Titulares',
        'estatisticas': 'Estatísticas e Conformidade'
      }[tipo] || tipo;
      
      currentY = this.adicionarTexto(doc, `${index + 1}. ${tipoLabel}`, currentY, margin + 10, availableWidth, 11);
    });
    
    currentY += 20;
    
    for (let i = 0; i < tipos.length; i++) {
      const tipo = tipos[i];
      
      try {
        if (i > 0) {
          doc.addPage();
          currentY = margin;
        }
        
        const dadosEspecificos = await this.buscarDadosRelatorio(clienteId, tipo, filtros);
        const dadosValidados = this.validarDadosRelatorio(dadosEspecificos);
        
        switch (tipo) {
          case 'geral':
            currentY = await this.gerarRelatorioGeral(doc, dadosValidados, currentY, margin, pageWidth, pageHeight, availableWidth);
            break;
          case 'questionario_detalhado':
            currentY = await this.gerarRelatorioQuestionario(doc, dadosValidados, currentY, margin, pageWidth, pageHeight, availableWidth);
            break;
          case 'titulares':
            currentY = await this.gerarRelatorioTitulares(doc, dadosValidados, currentY, margin, pageWidth, pageHeight, availableWidth);
            break;
          case 'estatisticas':
            currentY = await this.gerarRelatorioEstatisticas(doc, dadosValidados, currentY, margin, pageWidth, pageHeight, availableWidth);
            break;
        }
        
      } catch (sectionError) {
        currentY = this.adicionarTexto(doc, `Erro ao gerar seção ${tipo}: ${sectionError.message}`, currentY, margin, availableWidth, 12);
      }
    }
    
    this.adicionarRodape(doc, pageWidth, pageHeight, margin, clienteId);
    
    return new Blob([doc.output('arraybuffer')], { type: 'application/pdf' });
    
  } catch (error) {
    throw error;
  }
}

/**
 * Busca dados específicos para um tipo de relatório
 */
async buscarDadosRelatorio(clienteId, tipo, filtros = {}) {
  try {
    const response = await axios.post(
      `${this.API_BASE_URL}${this.MAPEAMENTOS_API_PREFIX}/relatorios/gerar`,
      {
        tipo,
        questionario_id: filtros.questionario_id,
        incluir_dados_sensiveis: filtros.incluir_dados_sensiveis ?? true
      },
      {
        headers: {
          ...this.getAuthHeaders(),
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        params: clienteId ? { cliente_id: clienteId } : {},
        timeout: 15000
      }
    );
    
    return response.data?.data || response.data || {};
  } catch (error) {
    return {};
  }
}

/**
 * Método para gerar relatório de comparação entre clientes
 */
async gerarRelatorioComparativo(clienteIds = [], filtros = {}) {
  try {
    if (!Array.isArray(clienteIds) || clienteIds.length < 2) {
      throw new Error('Pelo menos 2 clientes são necessários para comparação');
    }

    const doc = new jsPDF();
    const margin = 15;
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const availableWidth = pageWidth - (margin * 2);
    
    let currentY = await this.adicionarCabecalho(doc, 'comparativo', {
      ...filtros,
      titulo_personalizado: 'Relatório Comparativo - Mapeamento LGPD'
    }, {}, margin, pageWidth, null);
    
    const dadosClientes = await Promise.all(
      clienteIds.map(async (clienteId) => {
        const [dados, nomeCliente] = await Promise.all([
          this.buscarDadosRelatorio(clienteId, 'geral', filtros),
          this.recuperarInfoCliente(clienteId)
        ]);
        return { clienteId, nomeCliente, dados: this.validarDadosRelatorio(dados) };
      })
    );
    
    currentY = this.adicionarSecao(doc, "COMPARATIVO DE ESTATÍSTICAS", currentY, margin, pageHeight);
    
    const tabelaComparativa = [
      ["Métrica", ...dadosClientes.map(c => c.nomeCliente || c.clienteId.substring(0, 12))]
    ];
    
    const metricas = [
      'total_questionarios',
      'questionarios_concluidos', 
      'total_titulares',
      'dados_sensiveis',
      'dados_comuns',
      'areas_mapeadas'
    ];
    
    metricas.forEach(metrica => {
      const nomeMetrica = {
        'total_questionarios': 'Total Questionários',
        'questionarios_concluidos': 'Questionários Concluídos',
        'total_titulares': 'Total Titulares',
        'dados_sensiveis': 'Dados Sensíveis',
        'dados_comuns': 'Dados Comuns',
        'areas_mapeadas': 'Áreas Mapeadas'
      }[metrica] || metrica;
      
      const linha = [nomeMetrica];
      dadosClientes.forEach(cliente => {
        linha.push((cliente.dados.estatisticas[metrica] || 0).toString());
      });
      tabelaComparativa.push(linha);
    });
    
    currentY = this.adicionarTabela(doc, tabelaComparativa, currentY, margin, availableWidth, [255, 193, 7]);
    
    dadosClientes.forEach((cliente, index) => {
      currentY = this.verificarNovaPagina(doc, currentY, pageHeight, margin, 150);
      currentY = this.adicionarSecao(doc, `CLIENTE ${index + 1}: ${cliente.nomeCliente || cliente.clienteId}`, currentY, margin, pageHeight);
      
      const stats = cliente.dados.estatisticas;
      const taxaConclusao = stats.total_questionarios > 0 ? 
        Math.round((stats.questionarios_concluidos / stats.total_questionarios) * 100) : 0;
      
      const resumoCliente = [
        ["Aspecto", "Valor", "Status"],
        ["Taxa de Conclusão", `${taxaConclusao}%`, taxaConclusao >= 80 ? "✓" : "⚠"],
        ["Total de Titulares", stats.total_titulares.toString(), stats.total_titulares > 0 ? "✓" : "⚠"],
        ["Dados Sensíveis", stats.dados_sensiveis.toString(), stats.dados_sensiveis > 0 ? "Atenção" : "OK"],
        ["Cobertura", `${stats.areas_mapeadas} áreas`, stats.areas_mapeadas > 2 ? "✓" : "⚠"]
      ];
      
      currentY = this.adicionarTabela(doc, resumoCliente, currentY, margin, availableWidth, [76, 175, 80]);
    });
    
    this.adicionarRodape(doc, pageWidth, pageHeight, margin, 'comparativo');
    
    return new Blob([doc.output('arraybuffer')], { type: 'application/pdf' });
    
  } catch (error) {
    throw error;
  }
}

/**
 * Método para diagnóstico de problemas
 */
async diagnosticar(clienteId = null) {
  const diagnostico = {
    backend_disponivel: false,
    dados_acessiveis: false,
    cliente_valido: false,
    bibliotecas_carregadas: false,
    erros: [],
    avisos: [],
    sugestoes: []
  };
  
  try {
    try {
      const testDoc = new jsPDF();
      diagnostico.bibliotecas_carregadas = true;
    } catch (libError) {
      diagnostico.erros.push('jsPDF não está disponível');
    }
    
    try {
      diagnostico.backend_disponivel = await this.verificarDisponibilidade();
      if (!diagnostico.backend_disponivel) {
        diagnostico.avisos.push('Backend não está respondendo');
      }
    } catch (backendError) {
      diagnostico.erros.push(`Erro de conectividade: ${backendError.message}`);
    }
    
    if (clienteId) {
      try {
        const nomeCliente = await this.recuperarInfoCliente(clienteId);
        if (nomeCliente) {
          diagnostico.cliente_valido = true;
        } else {
          diagnostico.avisos.push('Cliente não encontrado');
        }
      } catch (clienteError) {
        diagnostico.erros.push(`Erro ao verificar cliente: ${clienteError.message}`);
      }
    }
    
    if (diagnostico.backend_disponivel) {
      try {
        const testeDados = await this.buscarDadosRelatorio(clienteId, 'geral');
        if (testeDados && Object.keys(testeDados).length > 0) {
          diagnostico.dados_acessiveis = true;
        } else {
          diagnostico.avisos.push('Nenhum dado encontrado');
        }
      } catch (dadosError) {
        diagnostico.erros.push(`Erro ao acessar dados: ${dadosError.message}`);
      }
    }
    
    if (!diagnostico.backend_disponivel) {
      diagnostico.sugestoes.push('Verificar conectividade com a API');
    }
    if (!diagnostico.dados_acessiveis && diagnostico.backend_disponivel) {
      diagnostico.sugestoes.push('Verificar se há dados cadastrados para o cliente');
    }
    if (diagnostico.erros.length === 0 && diagnostico.avisos.length === 0) {
      diagnostico.sugestoes.push('Sistema funcionando corretamente');
    }
    
  } catch (error) {
    diagnostico.erros.push(`Erro geral no diagnóstico: ${error.message}`);
  }
  
  return diagnostico;
}
}

export default new MapeamentosPdfGenerator();